/**
 * Painel geral (/): todos os canais conectados. De cima para baixo: alertas críticos (só quando há), resumo do dia
 * (projeção, vendido hoje, mês × meta, margem estimada), comparativo de canais (clicável: abre o painel do canal),
 * faturamento do mês por dia e canal (dia de hoje parcial, meta do dia tracejada), "o que fazer agora", catálogo por
 * canal e o espaço da rentabilidade (aparece com a conciliação). Só lê: nada aqui muda dado.
 * Identidade: Sora nos números e títulos, Inter no texto; o gradiente da marca só no número da projeção.
 */
import { Link, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api, brl, int } from '../api.ts';
import { ErrorBox, Loading } from '../components/ui.tsx';
import { useMe } from '../components/AuthGate.tsx';

type Level = 'ok' | 'attention' | 'critical';
interface ChannelRow {
  channel: string; name: string; level: Level; status: string;
  today: { amount: number; orders: number }; projection: number | null; pacePct: number | null;
  month: { amount: number; share: number | null; goal: number | null; goalPct: number | null; goalSource: string | null; provisional: boolean };
  margin: number | null; listings: { active: number; paused: number };
  price: { buyBoxPct: number | null; losing: number } | null; pmc: { above: number; near: number };
  pending: { catalog: number; risk: number };
}
export interface General {
  now: { today: string; time: string };
  alerts: { level: Level; title: string; detail: string; cta: string; to: string }[];
  summary: {
    projection: number | null; pacePct: number | null; refDays: string[]; average: { business: number; weekend: number } | null;
    today: { amount: number; orders: number; ticket: number | null };
    month: { month: string; amount: number; goal: number | null; goalPct: number | null };
    margin: { pct: number | null; best: { channel: string; pct: number } | null; worst: { channel: string; pct: number } | null };
  };
  channels: ChannelRow[];
  days: { date: string; partial: boolean; byChannel: Record<string, number>; goal: number | null }[];
  actions: { title: string; detail: string; channel: string; cta: string; to: string }[];
  catalog: { channel: string; name: string; published: number; ready: number; pending: number; reasons: { reason: string; n: number }[] }[];
  unavailable: string[];
}

/** Cor de cada canal nos gráficos (tokens de gráfico da marca); a cor do canal (amarelo do ML etc.) fica só no ícone da tabela. */
export const CHANNEL_COLOR: Record<string, string> = { mercadolivre: 'var(--series-meli)', shopee: 'var(--series-shopee)', ifood: 'var(--series-ifood)' };
const SWATCH: Record<string, string> = { mercadolivre: 'var(--ch-mercadolivre)', shopee: 'var(--ch-shopee)', ifood: 'var(--ch-ifood)' };
const NAME: Record<string, string> = { mercadolivre: 'Mercado Livre', shopee: 'Shopee', ifood: 'iFood' };
const MONTHS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const WEEKDAYS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
const pctTxt = (v: number | null | undefined, digits = 1) => (v == null ? '—' : `${v.toLocaleString('pt-BR', { maximumFractionDigits: digits })}%`);
const signed = (v: number) => `${v >= 0 ? '+' : '−'}${Math.abs(v).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
const brl0 = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const short = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;
const LEVEL_TONE: Record<Level, string> = { ok: 'ok', attention: 'warn', critical: 'err' };

/** Texto dos dias de referência ("ontem e a mesma terça da semana passada"). */
export function refText(today: string, refs: string[]) {
  const dayOf = (d: string) => new Date(`${d}T12:00:00Z`).getUTCDay();
  const yesterday = new Date(new Date(`${today}T12:00:00Z`).getTime() - 86_400_000).toISOString().slice(0, 10);
  return refs.map((d) => (d === yesterday ? 'ontem' : `${WEEKDAYS[dayOf(d)]!.toLowerCase()} ${short(d)}`)).join(' e ');
}

export function General() {
  const me = useMe();
  const q = useQuery({ queryKey: ['general'], queryFn: () => api<General>('/general'), refetchInterval: 120_000 });
  const d = q.data;
  const today = d ? new Date(`${d.now.today}T12:00:00Z`) : null;
  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">Todos os canais</div>
          <h1>Painel geral</h1>
          <p>{today ? `${WEEKDAYS[today.getUTCDay()]}, ${today.getUTCDate()} de ${MONTHS[today.getUTCMonth()]} · atualizado às ${d!.now.time}` : me.tenant.name}</p>
        </div>
      </div>
      {q.error ? <ErrorBox error={q.error} /> : !d ? <Loading /> : (
        <div className="stack-y" style={{ gap: 24 }}>
          {d.alerts.length > 0 && (
            <section aria-label="Alertas críticos" className="alerts-grid">
              {d.alerts.map((a, i) => (
                <div key={i} className={`alert-card ${LEVEL_TONE[a.level]}`}>
                  <span className="dot" aria-hidden />
                  <div style={{ flex: 1, minWidth: 0 }}><b>{a.title}</b><div className="small muted">{a.detail}</div></div>
                  <Link className="small strong" to={a.to}>{a.cta}</Link>
                </div>
              ))}
            </section>
          )}

          <section aria-label="Resumo do dia" className="kpis">
            <div className="kpi">
              <span className="k-label">Projeção do dia</span>
              <span className="k-value proj-number">{d.summary.projection == null ? '—' : brl0(d.summary.projection)}</span>
              {d.summary.pacePct != null && <span className="small strong" style={{ display: 'block', color: d.summary.pacePct >= 0 ? 'var(--ok)' : 'var(--err)' }}>{signed(d.summary.pacePct)} vs. ritmo de referência</span>}
              <span className="k-note">
                {d.summary.refDays.length ? `${refText(d.now.today, d.summary.refDays)}, até as ${d.now.time}` : 'Sem dias de referência'}
                {d.summary.average && <> · média das últimas 4 semanas: dia útil {brl0(d.summary.average.business)} · fim de semana {brl0(d.summary.average.weekend)}</>}
              </span>
            </div>
            <div className="kpi">
              <span className="k-label">Vendido hoje</span>
              <span className="k-value">{brl(d.summary.today.amount)}</span>
              <span className="k-note">{int(d.summary.today.orders)} pedido{d.summary.today.orders === 1 ? '' : 's'}{d.summary.today.ticket != null && <> · ticket médio {brl(d.summary.today.ticket)}</>}</span>
            </div>
            <div className="kpi">
              <span className="k-label" style={{ textTransform: 'capitalize' }}>{MONTHS[Number(d.summary.month.month.slice(5, 7)) - 1]}</span>
              <span className="k-value">{brl0(d.summary.month.amount)}</span>
              <div className="bar" aria-hidden><div style={{ width: `${Math.min(100, d.summary.month.goalPct ?? 0)}%` }} /></div>
              <span className="k-note">{d.summary.month.goal ? <>{pctTxt(d.summary.month.goalPct, 0)} da meta de {brl0(d.summary.month.goal)}</> : <>Sem meta: <Link to="/configuracoes/metas">definir metas</Link></>}</span>
            </div>
            <div className="kpi">
              <span className="k-label">Margem estimada do mês</span>
              <span className="k-value">{pctTxt(d.summary.margin.pct)}</span>
              <span className="k-note">
                {d.summary.margin.best ? <>Maior: {NAME[d.summary.margin.best.channel]} ({pctTxt(d.summary.margin.best.pct, 0)}){d.summary.margin.worst && <> · menor: {NAME[d.summary.margin.worst.channel]} ({pctTxt(d.summary.margin.worst.pct, 0)})</>}</>
                  : 'Aparece com os extratos e o custo dos itens'}
              </span>
            </div>
          </section>

          <ChannelTable rows={d.channels} />
          <MonthChart d={d} />

          <div className="split">
            <section aria-labelledby="t-fila" className="panel flush" style={{ flex: '999 1 560px', minWidth: 0 }}>
              <div className="panel-head"><h2 id="t-fila">O que fazer agora</h2><span className="small muted">Ordenado por impacto em todos os canais</span></div>
              {d.actions.length === 0 ? <div className="empty"><b>Nada urgente</b>Nenhuma ação pendente nos canais.</div> : d.actions.map((x, i) => (
                <div key={i} className="action-row">
                  <span className="mono small muted" style={{ width: 22 }}>{String(i + 1).padStart(2, '0')}</span>
                  <div style={{ flex: 1, minWidth: 220 }}><b>{x.title}</b><div className="small muted">{x.detail}</div></div>
                  <span className="tag">{x.channel}</span>
                  <Link className="small strong" to={x.to}>{x.cta}</Link>
                </div>
              ))}
            </section>
            <section aria-labelledby="t-cat" className="panel stack-y" style={{ flex: '1 1 360px', minWidth: 0, gap: 14 }}>
              <h2 id="t-cat" style={{ margin: 0 }}>Catálogo por canal</h2>
              <div className="legend small muted">
                <span><i style={{ background: 'var(--chart-1)' }} />Publicado</span><span><i style={{ background: 'var(--ok)' }} />Pronto</span><span><i style={{ background: 'var(--warn)' }} />Pendente</span>
              </div>
              {d.catalog.map((k) => {
                const t = k.published + k.ready + k.pending || 1;
                return (
                  <div key={k.channel} className="stack-y" style={{ gap: 6 }}>
                    <div className="small" style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                      <b>{k.name}</b><span className="muted">{int(k.published)} publicados · {int(k.ready)} prontos · {int(k.pending)} pendentes</span>
                    </div>
                    <div className="stack-bar" role="img" aria-label={`${k.name}: ${k.published} publicados, ${k.ready} prontos, ${k.pending} pendentes`}>
                      <div style={{ width: `${(k.published / t) * 100}%`, background: 'var(--chart-1)' }} />
                      <div style={{ width: `${(k.ready / t) * 100}%`, background: 'var(--ok)' }} />
                      <div style={{ width: `${(k.pending / t) * 100}%`, background: 'var(--warn)' }} />
                    </div>
                  </div>
                );
              })}
              <div className="notice" style={{ marginTop: 8 }}>
                <div className="eyebrow">Rentabilidade</div>
                <span className="small muted">Margem real por canal, itens mais rentáveis e itens com prejuízo aparecem aqui quando a conciliação estiver ativa.</span>
              </div>
            </section>
          </div>
          {d.unavailable.length > 0 && <p className="small muted" style={{ margin: 0 }}>Ainda sem dado: {d.unavailable.join(' ')}</p>}
        </div>
      )}
    </>
  );
}

function ChannelTable({ rows }: { rows: ChannelRow[] }) {
  const nav = useNavigate();
  return (
    <section aria-labelledby="t-canais" className="panel flush">
      <div className="panel-head"><h2 id="t-canais">Comparativo de canais</h2><span className="small muted">Clique em um canal para abrir o painel dele</span></div>
      {rows.length === 0 ? <div className="empty"><b>Nenhum canal conectado</b><Link to="/canais">Conectar um canal</Link></div> : (
        <div className="grid-wrap">
          <table className="grid" style={{ width: '100%', minWidth: 1080 }}>
            <thead><tr><th>Canal</th><th className="num">Hoje</th><th className="num">Projeção</th><th className="num">Mês</th><th className="num">Margem</th><th className="num">Anúncios</th><th>Preço</th><th className="num">Pendências</th></tr></thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.channel} onClick={() => nav(`/canal/${c.channel}`)} style={{ cursor: 'pointer' }}>
                  <td style={{ whiteSpace: 'normal', minWidth: 210 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span className="swatch" style={{ background: SWATCH[c.channel] }} aria-hidden />
                      <div className="cell-2" style={{ whiteSpace: 'normal' }}><Link to={`/canal/${c.channel}`} onClick={(e) => e.stopPropagation()} style={{ fontWeight: 600 }}>{c.name}</Link>
                        <span className="sub strong" style={{ color: c.level === 'ok' ? 'var(--ok)' : c.level === 'critical' ? 'var(--err)' : 'var(--warn)' }}>{c.status}</span></div>
                    </div>
                  </td>
                  <td className="num"><b>{brl0(c.today.amount)}</b><div className="small muted">{int(c.today.orders)} pedido{c.today.orders === 1 ? '' : 's'}</div></td>
                  <td className="num"><b>{c.projection == null ? '—' : brl0(c.projection)}</b>
                    {c.pacePct != null && <div className="small strong" style={{ color: c.pacePct >= 0 ? 'var(--ok)' : 'var(--err)' }}>{signed(c.pacePct)} no ritmo</div>}</td>
                  <td className="num" style={{ whiteSpace: 'normal', minWidth: 170 }}><b>{brl0(c.month.amount)}</b>
                    <div className="small muted">{pctTxt(c.month.share)}{c.month.goalPct != null && <> · {pctTxt(c.month.goalPct, 0)} da meta{c.month.provisional ? ' (provisória)' : ''}</>}</div></td>
                  <td className="num"><b>{pctTxt(c.margin, 0)}</b></td>
                  <td className="num"><b>{int(c.listings.active)} ativos</b><div className="small muted">{int(c.listings.paused)} pausados</div></td>
                  <td style={{ whiteSpace: 'normal', minWidth: 150 }}>{c.price ? <><b>{pctTxt(c.price.buyBoxPct, 0)} com buy box</b><div className="small muted">{int(c.price.losing)} perdendo</div></> : <><b>—</b><div className="small muted">sem monitor de preço</div></>}
                    {c.pmc.above > 0 && <div className="small" style={{ color: 'var(--err)' }}>{int(c.pmc.above)} acima do PMC</div>}</td>
                  <td className="num"><span className={`tag ${c.pending.risk ? 'err' : c.pending.catalog ? 'warn' : ''}`}
                    title={`${c.pending.catalog} pendentes de cadastro · ${c.pending.risk} pedidos em risco`}>{int(c.pending.catalog + c.pending.risk)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function MonthChart({ d }: { d: General }) {
  const channels = d.channels.map((c) => c.channel);
  const max = Math.max(1, ...d.days.map((x) => Math.max(Object.values(x.byChannel).reduce((a, v) => a + v, 0), x.goal ?? 0)));
  const H = 200;
  return (
    <section aria-labelledby="t-fat" className="panel stack-y" style={{ gap: 14 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
        <h2 id="t-fat" style={{ margin: 0 }}>Faturamento dos últimos 30 dias por canal</h2>
        <div className="legend small muted">
          {channels.map((c) => <span key={c}><i style={{ background: CHANNEL_COLOR[c] }} />{NAME[c]}</span>)}
          <span><i className="dash" />Meta do dia</span>
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <div className="month-chart" style={{ height: H + 20 }}>
          {d.days.map((x, i) => {
            const total = Object.values(x.byChannel).reduce((a, v) => a + v, 0);
            return (
              <div key={x.date} className="col" title={`${short(x.date)}: ${brl(total)}${x.goal != null ? ` · meta ${brl(x.goal)}` : ''}${x.partial ? ' (parcial)' : ''}`}>
                <div className="stack" style={{ height: H, opacity: x.partial ? 0.45 : 1 }}>
                  {channels.map((c) => <div key={c} style={{ height: `${((x.byChannel[c] ?? 0) / max) * H}px`, background: CHANNEL_COLOR[c] }} />)}
                </div>
                {x.goal != null && <div className="goal" style={{ bottom: `${20 + (x.goal / max) * H}px` }} />}
                <span className="mono">{i === 0 || x.date.endsWith('-01') ? `${Number(x.date.slice(8, 10))}/${x.date.slice(5, 7)}` : Number(x.date.slice(8, 10))}</span>
              </div>
            );
          })}
        </div>
      </div>
      <span className="small muted">Hoje é parcial (vendido até agora). A linha tracejada é a meta de cada dia: a meta do mês daquele dia dividida pelo peso de cada dia da semana, com desconto em feriado, emenda e eleição.</span>
    </section>
  );
}
